import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Alert,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import { INDUSTRIES } from '../../../constants';

const BasicsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
}));

interface CompanyBasicsStepProps {
  formData: any;
  updateFormData: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  appName: string;
}

export default function CompanyBasicsStep({ 
  formData, 
  updateFormData, 
  onNext, 
  onBack, 
  appName 
}: CompanyBasicsStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.industry) {
      newErrors.industry = 'Please select an industry';
    }

    if (!formData.website.trim()) {
      newErrors.website = 'Website URL is required';
    } else if (!isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid website URL with protocol (http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      // Require protocol - don't automatically add it
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return false;
      }
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const formatWebsiteUrl = (url: string) => {
    if (!url) return url;
    // Keep the URL as-is, including protocol
    return url.trim();
  };

  const handleWebsiteChange = (value: string) => {
    const formattedUrl = formatWebsiteUrl(value);
    updateFormData('website', formattedUrl);
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <BasicsContainer>
      <Fade in={true} timeout={800}>
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: 'text.primary'
              }}
            >
              Tell us about your company
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ lineHeight: 1.6 }}
            >
              This helps us customize {appName} for your specific business needs
            </Typography>
          </Box>

          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => updateFormData('companyName', e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName || 'What should we call your company?'}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color={errors.companyName ? 'error' : 'action'} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth required error={!!errors.industry}>
              <InputLabel>Industry</InputLabel>
              <Select
                value={formData.industry}
                label="Industry"
                onChange={(e) => updateFormData('industry', e.target.value)}
              >
                {INDUSTRIES.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
              {errors.industry && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.industry}
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Website"
              value={formData.website}
              onChange={(e) => handleWebsiteChange(e.target.value)}
              error={!!errors.website}
              helperText={errors.website || 'Your company website with protocol (e.g., https://example.com or http://www.example.com)'}
              fullWidth
              required
              placeholder="https://example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon color={errors.website ? 'error' : 'action'} />
                  </InputAdornment>
                ),
              }}
            />

            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Please fill in all required fields correctly to continue.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ 
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </BasicsContainer>
  );
}